import env from '@beam-australia/react-env';
import { isObject } from 'lodash';
import { hasMinimumLength, filterWithMinimumLength, replaceEmptyString } from '@/utils/resultsHelper';
import { cohortChart } from '@/cohorts/visualisations/constants';
import { historyEntities } from '@/audit/constants';

export const linkPrefixes = {
  HTTPS: 'https://',
  HTTP: 'http://',
};

const BASE_URL = '/data/';
const TAB_URL = '/tab/';
const KB_URL = `${linkPrefixes.HTTPS}knowledgebase.aridhia.io`;
const HOSTNAME = env('FAIR_UI_HOSTNAME');
const BASE_URLS = [BASE_URL, TAB_URL];

const stripHash = (url) => url.replace(/^#/, '').replace(/^\//, '');

export const locationInputs = {
  hash: () => stripHash(window.location.hash),
  hashParts: () => stripHash(window.location.hash).split('/'),
  hostParts: () => window.location.hostname.split('.'),
  params: () => new URLSearchParams(window.location.search),
  inFrame: () => window?.top !== window?.self,
  postMessage: (message, targetOrigin) => window.top.postMessage(message, targetOrigin),
};

const baseHashRoute = () => {
  const segment = `/${locationInputs.hashParts()[0]}/`;
  return BASE_URLS.includes(segment) ? segment : BASE_URL;
};

export const isTabURL = () => baseHashRoute() === TAB_URL;

const urls = {
  ACTIVITIES: 'activities/',
  API: `${HOSTNAME}/api/`,
  AUDIT: 'audits/',
  BASE: BASE_URL,
  CATALOGUE_TEMPLATE: 'catalogue-template/',
  CATALOGUE_TEMPLATES: 'catalogue-templates/',
  COHORT: 'cohort/',
  COHORTS: 'cohorts/',
  COLLECTION: 'collection/',
  COLLECTIONS: 'collections/',
  DATASET: 'dataset/',
  DATASETS: 'datasets/',
  DOCS: `${HOSTNAME}/api/docs`,
  HOME: 'home/',
  KB: KB_URL,
  KB_EDIT_DICTIONARY: `${KB_URL}/fair-data-services/managing-data/managing-metadata/editing-a-dataset`,
  LEARN: `${KB_URL}/kbcategory/fair-data-services/`,
  MY_TOKENS: 'tokens/',
  PRIVACY_POLICY: `${KB_URL}/privacy-policy/`,
  RESTRICTIONS: `${KB_URL}/workspaces/workspace-administration/workspace-restrictions`,
  REQUEST: 'request/',
  REQUESTS: 'requests/',
  REVIEW: 'review/',
  ROLES: 'roles/',
  SEARCH: 'search/',
  SEARCH_HELP: `${KB_URL}/article/searching-for-data/`,
  SEARCHES: 'searches/',
  SERVICE_DESK: `${KB_URL}/contact-service-desk/`,
  TAB: TAB_URL,
  TASKS: 'tasks/',
  THEMES: 'themes/',
  TOKENS: 'token-management/',
  T_AND_C: `${KB_URL}/terms-and-conditions/`,
  USERS: 'users/',
  USER_GROUP: 'usergroup/',
  USER_GROUPS: 'usergroups/',
  VOCABULARIES: 'vocabularies/',
  WORKSPACE: 'workspace/',
};

export const joinUrls = (str1, str2) => {
  const trimmedStr1 = str1.replace(/\/$/, '');
  const trimmedStr2 = str2.replace(/^\//, '');

  return `${trimmedStr1}/${trimmedStr2}`;
};

export const relativeUrl = (to) => joinUrls(baseHashRoute(), to.replace(/^(\/?(data\/|tab\/))/, ''));

export const relativeUrls = (to) => {
  const url = relativeUrl(to);
  return [url.replace(/\/$/, ''), url.replace(/\/?$/, '/')];
};

export const splitUrlArguments = (url) => filterWithMinimumLength(url.split('/'));
export const joinUrlArguments = (args) => `/${filterWithMinimumLength(args).join('/')}`;

export const codeUrl = (base, code, prefix = '') => `${prefix}${relativeUrl(base + encodeURIComponent(code))}`;
export const searchUrl = (search, prefix = '') => codeUrl(urls.SEARCHES, search.code, prefix);
export const cohortUrl = (cohort, prefix = '') => codeUrl(urls.COHORTS, cohort.code, prefix);
export const collectionUrl = (collection, prefix = '') => codeUrl(urls.COLLECTIONS, collection.code, prefix);
export const datasetUrl = (dataset, prefix = '') => codeUrl(urls.DATASETS, dataset.code, prefix);
export const dictionaryUrl = (dataset, dictionary, prefix = '') => (
  `${datasetUrl(dataset, prefix)}/dictionaries/${encodeURIComponent(dictionary.code)}`
);
export const dictionaryMarkdownUrl = (dataset, dictionary, prefix = '') => (
  `${dictionaryUrl(dataset, dictionary, prefix)}/markdown`
);
export const datasetAdminUrl = (dataset, prefix = '') => `${datasetUrl(dataset, prefix)}/settings`;
export const datasetMarkdownUrl = (dataset, prefix = '') => `${datasetUrl(dataset, prefix)}/markdown`;
export const requestUrl = (request, prefix = '') => codeUrl(urls.REQUESTS, request.code, prefix);
export const requestMarkdownUrl = (request, prefix = '') => `${requestUrl(request, prefix)}/markdown`;
export const kbUrl = (value) => `${KB_URL}/search?s=FAIR+${encodeURIComponent(value)}`;
export const roleUrl = (role, prefix = '') => codeUrl(urls.ROLES, role.code, prefix);
export const userUrl = (user, prefix = '') => codeUrl(urls.USERS, user.uuid, prefix);
export const userGroupUrl = (userGroup, prefix = '') => codeUrl(urls.USER_GROUPS, userGroup.code, prefix);
export const newUserGroupUrl = (userGroup, uuid, prefix = '') => `${userGroupUrl(userGroup, prefix)}/usergroup/${uuid}`;
export const taskUrl = (task, prefix = '') => codeUrl(urls.TASKS, task.code, prefix);

export const catalogueTemplateUrl = (template, prefix = '') => (
  codeUrl(urls.CATALOGUE_TEMPLATES, template.code, prefix)
);

export const entityUrl = (entity, code, prefix = '') => ({
  search: searchUrl({ code }, prefix),
  cohort: cohortUrl({ code }, prefix),
  dataset: datasetUrl({ code }, prefix),
  request: requestUrl({ code }, prefix),
  role: roleUrl({ code }, prefix),
  usergroup: userGroupUrl({ code }, prefix),
}[entity]);

export const newRequestUrl = (entity, code, uuid, prefix = '') => ({
  cohort: `${cohortUrl({ code }, prefix)}/request/${uuid}`,
  dataset: `${datasetUrl({ code }, prefix)}/request/${uuid}`,
  request: `${requestUrl({ code }, prefix)}/request/${uuid}`,
}[entity]);

export const newDatasetUrl = (entity, code, uuid, prefix = '') => ({
  dataset: `${datasetUrl({ code }, prefix)}/dataset/${uuid}`,
  'catalogue-template': `${catalogueTemplateUrl({ code }, prefix)}/dataset/${uuid}`,
}[entity]);

export const subscribersUrl = (entity, code, prefix = '') => ({
  dataset: `${datasetUrl({ code }, prefix)}`,
}[entity]);

export const notificationsUrl = (entity, code, prefix = '') => ({
  dataset: `${datasetUrl({ code }, prefix)}`,
}[entity]);

const urlFromRoute = (route, entities) => {
  const parts = route.split('/');
  const index = route.indexOf(entities);
  return (index >= 0 && index < parts.length - 1) ? parts[index + 1] : '';
};

export const entityUrlFromRoute = (entity, route, prefix = '') => {
  const entities = {
    cohort: { name: 'cohorts', url: urls.COHORTS },
    collection: { name: 'collections', url: urls.COLLECTIONS },
    dataset: { name: 'datasets', url: urls.DATASETS },
    request: { name: 'requests', url: urls.REQUESTS },
    role: { name: 'roles', url: urls.ROLES },
    search: { name: 'searches', url: urls.SEARCHES },
    user: { name: 'users', url: urls.USERS },
    usergroup: { name: 'usergroups', url: urls.USER_GROUPS },
  }[entity] || { name: '', url: '' };

  const code = urlFromRoute(route || '', entities.name);
  return code ? codeUrl(entities.url, code, prefix) : null;
};

export const pathsMatch = (path1, path2) => path1.toLowerCase().replace(/\/$/, '')
  === path2.toLowerCase().replace(/\/$/, '');

export const startsWithUrl = (url, string) => {
  if (url.startsWith(TAB_URL) && string.startsWith(BASE_URL)) {
    const toCompare = url.replace(TAB_URL, BASE_URL);
    return toCompare.startsWith(string);
  }

  return url.startsWith(string);
};

export const endpoints = {
  ANNOUNCEMENTS: '/service-announcements/',
  API_IMAGES: '/api/images/',
  AUDIT: '/audits/',
  CATALOGUE_TEMPLATES: '/templates/catalogues/',
  COHORTS: '/cohorts/',
  COLLECTIONS: '/collections/',
  CONDITIONS: '/conditions/',
  CONFIGURATIONS: '/configurations/',
  CURRENT_SEARCH_CONFIG: '/search/current-configuration',
  DATA_MODELS: '/datamodels/',
  DATASETS: '/datasets/',
  DATASOURCES: '/datasources/',
  DATASOURCE_TYPES: '/datasourcetypes/',
  DICTIONARIES: '/dictionaries/',
  DOCS: '/api/docs/',
  SELECT: '/selection/select',
  VALIDATE: '/selection/validate',
  FILES_DOWNLOAD_DATASETS: '/files/datasets/',
  FILES_UPLOAD_DATASETS: '/api/files/datasets/',
  FILES_UPLOAD_DICTIONARIES: '/api/files/dictionaries/',
  GUEST_USER: '/users/guest',
  SELF_USER: '/users/self',
  IMAGES: '/images/',
  PMN_FUNCTIONS: '/pseudonymisation-functions/',
  REQUEST_TASKS: '/tasks/',
  REQUEST_TASK_VIEWS: '/taskqueries/',
  REQUESTS: '/requests/',
  ROLES: '/roles/',
  ROLE_ACTION_IMAGES: '/api/images/role-actions-images/',
  METRICS: '/metrics/',
  SEARCH: '/search/search/',
  SEARCH_CONFIG: '/search/configurations/',
  SEARCHES: '/searches/',
  SELECTION_VISUALISATION_BARCHART: '/selection/visualisation/bar-chart',
  SELECTION_VISUALISATION_BOXPLOT: '/selection/visualisation/box-plot',
  SELECTION_COHORT_COUNTS: '/selection/cohort-counts/',
  SELECTION_VISUALISATION_HISTOGRAM: '/selection/visualisation/histogram',
  SELECTION_VISUALISATION_STACKED_BARCHART: '/selection/visualisation/bar-chart',
  TASK_TEMPLATES: '/templates/tasks/',
  THEMES: '/themes/',
  TOKEN_ROLES: '/tokenroles/',
  TOKENS: '/tokens/',
  USERS: '/users/',
  USER_GROUPS: '/usergroups/',
  VOCABULARIES: '/vocabularies/',
  WORKFLOWS: '/workflows/',
  WORKSPACES: '/workspaces/',
  WORKSPACES_FORMS: '/workspaces-forms/',
  WORKSPACES_LOCATIONS: '/workspaces-locations/',
};

const codeEndpoint = (base, code) => `${base}${encodeURIComponent(code)}`;
export const searchEndpoint = (code) => codeEndpoint(endpoints.SEARCHES, code);
export const searchConfigEndpoint = (code) => `${endpoints.SEARCH_CONFIG}${encodeURIComponent(code)}/uiconfig`;
export const cohortEndpoint = (code) => codeEndpoint(endpoints.COHORTS, code);
export const collectionDatasetsEndpoint = (code) => `${codeEndpoint(endpoints.COLLECTIONS, code)}/datasets`;
export const collectionDatasetsManagementEndpoint = (code, datasetCode) => (
  `${codeEndpoint(endpoints.COLLECTIONS, code)}/datasets/${encodeURIComponent(datasetCode)}`
);
export const collectionEndpoint = (code) => codeEndpoint(endpoints.COLLECTIONS, code);
export const collectionLogoEndpoint = (code) => `api${collectionEndpoint(code)}/logo`;
export const datasetEndpoint = (code) => codeEndpoint(endpoints.DATASETS, code);
export const dictionaryEndpoint = (code) => codeEndpoint(endpoints.DICTIONARIES, code);

const subDictionaryEndpoint = (datasetCode, dictionaryCode, suffix) => (
  `${datasetEndpoint(datasetCode)}${dictionaryEndpoint(dictionaryCode)}/${suffix}`
);

export const dictionaryMarkdownEndpoint = (datasetCode, dictionaryCode) => (
  subDictionaryEndpoint(datasetCode, dictionaryCode, 'markdown')
);

export const dictionaryFieldsEndpoint = (datasetCode, dictionaryCode) => (
  subDictionaryEndpoint(datasetCode, dictionaryCode, 'fields')
);

export const dictionaryLookupsEndpoint = (datasetCode, dictionaryCode) => (
  subDictionaryEndpoint(datasetCode, dictionaryCode, 'lookups')
);

export const dictionaryLookupOptionsEndpoint = (datasetCode, dictionaryCode, name) => (
  `${dictionaryLookupsEndpoint(datasetCode, dictionaryCode)}/${encodeURIComponent(name)}/options`
);

export const conditionEndpoint = (code) => codeEndpoint(endpoints.CONDITIONS, code);
export const requestEndpoint = (code) => codeEndpoint(endpoints.REQUESTS, code);
export const themeEndpoint = (code) => codeEndpoint(endpoints.THEMES, code);
export const configurationEndpoint = (code) => codeEndpoint(endpoints.CONFIGURATIONS, code);
export const vocabularyEndpoint = (code) => codeEndpoint(endpoints.VOCABULARIES, code);
export const roleEndpoint = (code) => codeEndpoint(endpoints.ROLES, code);
export const updateRoleEndpoint = (code) => `${roleEndpoint(code)}/assign`;
export const workflowEndpoint = (code) => codeEndpoint(endpoints.WORKFLOWS, code);
export const tokenEndpoint = (code) => codeEndpoint(endpoints.TOKENS, code);
export const pmnFunctionEndpoint = (code) => codeEndpoint(endpoints.PMN_FUNCTIONS, code);
export const catalogueEndpoint = (code) => codeEndpoint(endpoints.CATALOGUES, code);
export const catalogueTemplateEndpoint = (code) => codeEndpoint(endpoints.CATALOGUE_TEMPLATES, code);
export const workspaceFormEndpoint = (code) => codeEndpoint(endpoints.WORKSPACES_FORMS, code);
export const userGroupEndpoint = (code) => codeEndpoint(endpoints.USER_GROUPS, code);
export const updateUserGroupEndpoint = (code) => `${userGroupEndpoint(code)}/assign`;
export const userEndpoint = (id) => codeEndpoint(endpoints.USERS, id);
export const userTokensEndpoint = ({ id }) => `${userEndpoint(id)}/tokens`;
export const userTokenEndpoint = (user, code) => codeEndpoint(`${userTokensEndpoint(user)}/`, code);
export const taskEndpoint = (code) => codeEndpoint(endpoints.REQUEST_TASKS, code);
export const taskViewEndpoint = (code) => codeEndpoint(endpoints.REQUEST_TASK_VIEWS, code);
export const taskTemplateEndpoint = (code) => `${taskEndpoint(code)}/template`;
export const taskRequestEndpoint = (code) => `${taskEndpoint(code)}/request`;
export const taskClaimEndpoint = (code) => `${taskEndpoint(code)}/claim`;
export const taskManageEndpoint = (code) => `${taskEndpoint(code)}/manage`;
export const conditionsEndpoint = (entity, code) => {
  const items = { dataset: datasetEndpoint, cohort: cohortEndpoint };
  const endpoint = items[entity] || items.dataset;

  return `${endpoint(code)}/conditions`;
};

export const workspacesEndpoint = (entity, code) => {
  const endpoint = { dataset: datasetEndpoint, cohort: cohortEndpoint }[entity];
  return endpoint ? `${endpoint(code)}${endpoints.WORKSPACES}` : endpoints.WORKSPACES;
};

export const workspaceLocationsEndpoint = (entity, code) => {
  const endpoint = { dataset: datasetEndpoint, cohort: cohortEndpoint }[entity];
  return endpoint ? `${endpoint(code)}${endpoints.WORKSPACES_LOCATIONS}` : endpoints.WORKSPACES_LOCATIONS;
};

export const auditEndpoint = (entity, code) => {
  const endpoint = {
    [historyEntities.COHORT]: cohortEndpoint,
    [historyEntities.COLLECTION]: collectionEndpoint,
    [historyEntities.DATASET]: datasetEndpoint,
    [historyEntities.REQUEST]: requestEndpoint,
    [historyEntities.ROLE]: roleEndpoint,
    [historyEntities.SEARCH]: searchEndpoint,
    [historyEntities.TASK]: taskEndpoint,
    [historyEntities.USER]: userEndpoint,
    [historyEntities.USERGROUP]: userGroupEndpoint,
  }[entity];
  return endpoint ? `${endpoint(code)}/audit` : endpoints.AUDIT;
};

export const datasetSubscriptionsEndpoint = (code, event = null) => {
  const prefix = `${datasetEndpoint(code)}/subscriptions`;
  return event ? `${prefix}/${encodeURIComponent(event)}` : prefix;
};

export const datasetSubscribersEndpoint = (code, event) => (
  `${datasetEndpoint(code)}/subscribers/${encodeURIComponent(event)}`
);

export const subscriptionsEndpoint = (event) => `subscriptions/${encodeURIComponent(event)}`;

export const notificationsEndpoint = (entity, code, event) => {
  const endpoint = { dataset: datasetEndpoint }[entity];
  return endpoint ? `${endpoint(code)}/notifications/${encodeURIComponent(event)}` : '';
};

export const membersEndpoint = (entity, code) => {
  const endpoint = {
    cohort: cohortEndpoint,
    collection: collectionEndpoint,
    dataset: datasetEndpoint,
    search: searchEndpoint,
  }[entity];
  return endpoint ? `${endpoint(code)}/members` : endpoints.USERS;
};

export const userGroupMembersEndpoint = (userGroupCode, userUUID = null) => {
  const url = `${userGroupEndpoint(userGroupCode)}/users`;
  return userUUID ? `${url}/${userUUID}` : url;
};

export const userGroupsEndpoint = (entity, code, userGroup = null) => {
  const endpoint = {
    cohort: cohortEndpoint,
    collection: collectionEndpoint,
    dataset: datasetEndpoint,
    search: searchEndpoint,
  }[entity];
  const url = endpoint ? `${endpoint(code)}/usergroups` : endpoints.USER_GROUPS;
  return userGroup ? joinUrls(url, userGroup) : url;
};

export const usersUserGroupsEndpoint = (uuid) => (`${userEndpoint(uuid)}/usergroups`);

export const usersEndpoint = (entity, code, userId = null) => {
  const endpoint = {
    cohort: cohortEndpoint,
    collection: collectionEndpoint,
    dataset: datasetEndpoint,
    search: searchEndpoint,
  }[entity];
  const url = endpoint ? `${endpoint(code)}/users` : endpoints.USERS;

  return userId ? joinUrls(url, userId.toString()) : url;
};

export const permissionsEndpoint = (entity, code) => {
  const items = {
    dataset: datasetEndpoint,
    cohort: cohortEndpoint,
    collection: collectionEndpoint,
    search: searchEndpoint,
  };
  const endpoint = items[entity] || items.dataset;
  return `${endpoint(code)}/permissions`;
};

const cohortVisualisationEndPoints = {
  [cohortChart.BAR]: { name: 'bar-chart', endpoint: endpoints.SELECTION_VISUALISATION_BARCHART },
  [cohortChart.STACKED_BAR]: { name: 'bar-chart', endpoint: endpoints.SELECTION_VISUALISATION_STACKED_BARCHART },
  [cohortChart.BOX_PLOT]: { name: 'box-plot', endpoint: endpoints.SELECTION_VISUALISATION_BOXPLOT },
  [cohortChart.HISTOGRAM]: { name: 'histogram', endpoint: endpoints.SELECTION_VISUALISATION_HISTOGRAM },
};

export const cohortVisualisationEndpoint = (chart, code) => {
  const { name } = cohortVisualisationEndPoints[chart] || cohortVisualisationEndPoints[cohortChart.BAR];
  return `${cohortEndpoint(code)}/visualisation/${name}`;
};

export const systemCohortVisualisationEndpoint = (chart, code) => {
  const { name } = cohortVisualisationEndPoints[chart] || cohortVisualisationEndPoints[cohortChart.BAR];
  return `${requestEndpoint(code)}/cohort/visualisation/${name}`;
};

export const cohortSelectionVisualisationEndpoint = (chart) => (
  cohortVisualisationEndPoints[chart]?.endpoint || cohortVisualisationEndPoints[cohortChart.BAR].endpoint
);

export const userApprovedEndpoint = (approved) => (approved
  ? `${endpoints.USERS}approve`
  : `${endpoints.USERS}unapprove`);

export const uploadEndpointDatasetAttachments = (code, id = null) => (id
  ? `${endpoints.FILES_UPLOAD_DATASETS}${encodeURIComponent(code)}/attachments/${encodeURIComponent(id)}`
  : `${endpoints.FILES_UPLOAD_DATASETS}${encodeURIComponent(code)}/attachments`);

export const attachmentsEndpoint = (code, attachmentId = null) => (attachmentId
  ? `${datasetEndpoint(code)}/attachments/${encodeURIComponent(attachmentId)}`
  : `${datasetEndpoint(code)}/attachments`);

export const datasetFolderEndpoint = (code, folderPath) => (
  `${datasetEndpoint(code)}/folders/${encodeURIComponent(folderPath)}`
);

export const attachmentDictionaryEndpoint = (code, attachmentId) => (
  `${attachmentsEndpoint(code, attachmentId)}/dictionary`
);

export const attachmentDictionaryIngestEndpoint = (code, attachmentId) => (
  `${attachmentsEndpoint(code, attachmentId)}/ingest`
);

export const downloadEndpointDatasetAttachments = (code, id) => (
  `${endpoints.FILES_DOWNLOAD_DATASETS}${encodeURIComponent(code)}/attachments/${encodeURIComponent(id)}`
);

export const uploadEndpointDictionaryData = (dictionaryCode, dataId = null) => (dataId
  ? `${endpoints.FILES_UPLOAD_DICTIONARIES}${encodeURIComponent(dictionaryCode)}/data/${dataId}`
  : `${endpoints.FILES_UPLOAD_DICTIONARIES}${encodeURIComponent(dictionaryCode)}/data`);

export const dataModelReportsEndpoint = (code, reportId = null) => (reportId
  ? `${datasetEndpoint(code)}/reports/datamodel/${reportId}`
  : `${datasetEndpoint(code)}/reports/datamodel`);

export const datasetCollectionsEndpoint = (code) => `${datasetEndpoint(code)}/collections`;

export const datasetConversionEndpoint = (code) => `${datasetEndpoint(code)}/convert_data`;

export const datasetValidationEndpoint = (code) => `${datasetEndpoint(code)}/validate`;

export const dictionaryConversionEndpoint = (dictionaryCode) => `${dictionaryEndpoint(dictionaryCode)}/convert_data`;

export const dictionaryValidationEndpoint = (dictionaryCode) => `${dictionaryEndpoint(dictionaryCode)}/validate`;

export const dictionaryReportsEndpoint = (dictionaryCode, reportType, reportId = null) => (reportId
  ? `${dictionaryEndpoint(dictionaryCode)}/reports/${reportType}/${reportId}`
  : `${dictionaryEndpoint(dictionaryCode)}/reports/${reportType}`);

export const joinUrl = (value) => (hasMinimumLength(value.link) ? `${value.protocol}${value.link}` : null);

export const isSplitUrl = (value) => {
  const keys = isObject(value) ? Object.keys(value) : [];
  return (keys.length === 2 && keys.includes('link') && keys.includes('protocol'));
};

export const joinUrlOrNullify = (value) => (isSplitUrl(value) ? joinUrl(value) : replaceEmptyString(value));

export const splitUrl = (value, defaultValue = linkPrefixes.HTTPS) => {
  const lowered = value ? value.toLowerCase() : '';
  const parts = { protocol: defaultValue, link: value };

  if (lowered.startsWith(linkPrefixes.HTTPS)) {
    parts.protocol = linkPrefixes.HTTPS;
    parts.link = value.substring(8);
  } else if (lowered.startsWith(linkPrefixes.HTTP)) {
    parts.protocol = linkPrefixes.HTTP;
    parts.link = value.substring(7);
  }

  return parts;
};

export const prefixUrl = (href) => {
  const lowered = href ? href.toLowerCase() : linkPrefixes.HTTPS;

  if (lowered.startsWith(linkPrefixes.HTTP) || lowered.startsWith(linkPrefixes.HTTPS)) {
    return href;
  }
  return `${linkPrefixes.HTTPS}${href}`;
};

const urlMatchers = (pattern) => [
  new RegExp(`^/?${pattern}/?$`),
  new RegExp(`^/?data/${pattern}/?$`),
  new RegExp(`^/?tab/${pattern}/?$`),
];

export const matchSingleUrl = (paths) => (
  urlMatchers(paths.slice(0, -1).map((route) => `${route}/[^/]+`).concat(paths.slice(-1)).join('/'))
);

export const matchCodeUrl = (paths) => (
  urlMatchers(paths.map((route) => `${route}/[^/]+`).join('/'))
);

export default urls;
